import { get, set } from './storage-util';
import { logger } from './logger';
import { Raw, scheduleTypes } from 'rewrite-common';

const BLOCKS_SYNC_TIMESTAMP_DIFF = 'krt_blocks_sync_timestamp_diff';

/**
 * browser と server 間の時間を同期するための関数。
 * 時間を同期するため、"非同期"で、
 *   diff := browser time - server time
 * を localstorage に保存する。
 *
 * それで、synced browser time := browser time + diff = server time
 * を計算すればよい。
 */
function syncDateTimeWithServer({ trackUrl }: { trackUrl: string }) {
  const setToLocal = set(window.localStorage);
  const xhr = new XMLHttpRequest();
  xhr.open('GET', `${trackUrl}timestamp`);
  xhr.onerror = () => {
    window.console.error(`[blocks.timestamp] error status: ${xhr.status}`);
    setToLocal(BLOCKS_SYNC_TIMESTAMP_DIFF, 0, true);
  };
  xhr.onload = () => {
    try {
      const serverTimestamp = new Date(xhr.responseText);
      const diff = Date.now() - serverTimestamp.getTime();
      setToLocal(BLOCKS_SYNC_TIMESTAMP_DIFF, diff, true);
      logger.info(`[blocks.timestamp] synced: ${diff}`);
    } catch (e) {
      setToLocal(BLOCKS_SYNC_TIMESTAMP_DIFF, 0, true);
    }
  };
  xhr.send();

  return {
    toValue() {
      return getDateTimeSyncedWithServer(Date.now());
    },
  };
}

/**
 * syncDateTimeWithServer によって、browser と server 間の時間が同期済みであれば、
 *   browser time + (server time - browser time) を返す。
 * 動機済みでなければ、
 *   browser time を返す。
 *
 * 想定されるケース
 * 1. 正常系: 初回時 → browser time
 * 2. 正常系: 前回Sync済み → synced browser time
 * 4. 異常系: localStorageのDiffが改ざんされた場合 → browser time
 */
function getDateTimeSyncedWithServer(dateTime: number): number {
  const getFromLocal = get(window.localStorage);
  const diff = getFromLocal<number>(BLOCKS_SYNC_TIMESTAMP_DIFF);
  if (!diff || isNaN(diff)) return dateTime;
  return dateTime + diff;
}

/**
 * 現在時刻が配信スケジュールの期間内かどうか判定する
 * 配信期間 > 繰り返し設定 > 時間帯設定 の順に確認する
 *
 * 配信期間 (scheduleTimeRange) の想定されるパターン:
 * - 開始日も終了日も指定: [string, string]
 * - 開始日のみ指定: [string, '']
 * - 終了日のみ指定: ['', string]
 * - 開始日も終了日も指定しない: []
 */
function isOn(
  scheduleTimeRange: readonly string[] | undefined,
  recurrenceSettings: Raw.RecurrenceSettings | undefined,
): boolean {
  // 旧スケジュール配信画面で設定された場合
  if (!recurrenceSettings) {
    return _isOn(scheduleTimeRange);
  }

  const current = getDateTimeSyncedWithServer(Date.now());

  // 配信期間が設定されていないパターン
  // FIXME: ちょい後方互換として undefined も許容
  if (!scheduleTimeRange || scheduleTimeRange.length < 2) {
    return isOnRecurring(recurrenceSettings, current);
  }

  const [startDate, endDate] = scheduleTimeRange;
  const start = startDate === '' ? null : new Date(startDate).getTime();
  const end = endDate === '' ? null : new Date(endDate).getTime();

  // 開始日・終了日どちらかが設定されているパターン
  if (start === null && end != null && end > current) {
    return isOnRecurring(recurrenceSettings, current);
  }
  if (end === null && start != null && start < current) {
    return isOnRecurring(recurrenceSettings, current);
  }

  // 開始日・終了日どちらも指定されているパターン
  if (start != null && end != null && start < current && current < end) {
    return isOnRecurring(recurrenceSettings, current);
  }

  return false;
}

/**
 * 旧スケジュール配信画面において、現在時刻が配信スケジュールの期間内かどうか判定する
 */
function _isOn(scheduleTimeRange: readonly string[] | undefined): boolean {
  // 配信期間が設定されていなかったら常時公開
  if (!scheduleTimeRange || scheduleTimeRange.length < 2) {
    return true;
  }
  // 旧スケジュール配信では開始時間・終了時間の個別指定はできないので unreachable
  if (!scheduleTimeRange[0] || !scheduleTimeRange[1]) {
    return true;
  }
  const current = getDateTimeSyncedWithServer(Date.now());
  const [startDate, endDate] = scheduleTimeRange;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (start < current && current < end) {
    return true;
  }
  return false;
}

/**
 * 繰り返し設定を参照して、現在の日付が配信期間内かどうか判定する
 *
 * - 毎日: dist_days_and_hours のみ（全曜日）
 * - 毎週: dist_days_and_hours のみ
 * - 毎月: dist_days_and_hours（全曜日）+ recurring_date_setting.dates（指定日）
 */
function isOnRecurring(recurrenceSettings: Raw.RecurrenceSettings, current: number): boolean {
  const currentDate = new Date(current);
  // distDaysAndHours / daysOfWeek はローカル時間基準で保存されるため、ローカル時間で判定する
  const currentDayOfWeek = currentDate.getDay(); // Sun: 0 - Sat: 6

  switch (recurrenceSettings.type) {
    case 'daily':
      return isOnTime(recurrenceSettings.distDaysAndHours, currentDate);

    case 'weekly':
      if (!recurrenceSettings.daysOfWeek.includes(currentDayOfWeek as scheduleTypes.DayOfWeek)) {
        return false;
      }
      return isOnTime(recurrenceSettings.distDaysAndHours, currentDate);

    case 'monthly': {
      const currentDayOfMonth = getDayOrNegativeOneIfLastDay(currentDate);
      if (!recurrenceSettings.daysOfMonth.includes(currentDayOfMonth as scheduleTypes.DayOfMonth)) {
        return false;
      }
      return isOnTime(recurrenceSettings.distDaysAndHours, currentDate);
    }

    default:
      return false; // unreachable
  }
}

/**
 * 現在時刻が配信時間帯に含まれているかどうか判定する
 */
function isOnTime(
  distDaysAndHours: readonly scheduleTypes.IDaysAndHours[] | undefined,
  currentDate: Date,
): boolean {
  if (!distDaysAndHours || distDaysAndHours.length === 0) {
    return false;
  }

  // 日曜日0時からの経過秒数を計算
  const currentOffset =
    currentDate.getDay() * 24 * 60 * 60 +
    currentDate.getHours() * 60 * 60 +
    currentDate.getMinutes() * 60 +
    currentDate.getSeconds();

  return distDaysAndHours.some(range => {
    return range.start <= currentOffset && currentOffset < range.end;
  });
}

/**
 * 日付がその月の最終日かどうかをチェックし、最終日なら-1を、そうでなければその日を返す
 */
function getDayOrNegativeOneIfLastDay(currentDate: Date): number {
  // distDaysAndHours / daysOfMonth はローカル時間基準で保存されるため、ローカル時間で判定する
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1, // 翌月
    0, // 0日目 (=前月の最終日)
  ).getDate();

  const current = currentDate.getDate();
  if (current === lastDayOfMonth) {
    return -1;
  } else {
    return current;
  }
}

export {
  isOn,
  getDateTimeSyncedWithServer,
  syncDateTimeWithServer,
  BLOCKS_SYNC_TIMESTAMP_DIFF,
  isOnRecurring,
  isOnTime,
  getDayOrNegativeOneIfLastDay,
};
